import { apiPost } from './client'
import { offlineCardSource } from '../lib/offline/cardSource'

// Mirrors the API's POST /cards/images contract (the iOS structs match these
// field names exactly). Clients render these direct cards.scryfall.io URLs and
// let the browser HTTP cache hold the image bytes - no fuzzy redirect.
export interface CardFace {
  name: string
  mana_cost?: string | null
  type_line?: string | null
  oracle_text?: string | null
  image_normal?: string | null
  image_large?: string | null
}

// The owned printing the API resolved for this name (server owned-printings
// flag ON and a collection scope only). Same object on browse cards
// and on /cards/images cards. null when the collection owns no resolved
// printing of the name; absent entirely under the server flag-off path.
export interface Printing {
  id: string | null
  set: string | null
  set_name: string | null
  collector_number: string | null
  rarity: string | null
  finish: 'nonfoil' | 'foil' | 'etched'
  released_at: string | null
}

export interface CardImage {
  name: string
  layout?: string | null
  // Primary type line; lets the gallery + editor group by type (matches iOS).
  type_line?: string | null
  // TCGPlayer product id for direct affiliate buy links; null falls back to a
  // search URL (tcgCardUrl handles both).
  tcgplayer_id?: number | null
  // Representative-printing USD price (matches the build response's per-card
  // price); null when unpriced. Powers the saved/library "Cards to buy" total.
  price_usd?: number | null
  image_normal?: string | null
  image_large?: string | null
  // Present only for flip-capable layouts (transform/modal_dfc).
  faces?: CardFace[] | null
  // When present, image_normal / image_large / faces / price_usd already come
  // from this printing's row. Additive; consumers render it only behind an
  // explicit prop, so a flag-on server never changes a flag-off client's DOM
  // beyond the substituted image and price it already rendered.
  printing?: Printing | null
  // Additive deck-editor metadata (mtg-api 2026-10). Every field is optional:
  // an API older than these returns none of them, and the editor's curve,
  // pips and average show a dash until they arrive. mana_cost and colors come
  // from the PRIMARY face of a transform/MDFC card.
  cmc?: number | null
  mana_cost?: string | null
  colors?: string[] | null
  color_identity?: string[] | null
  // The raw Scryfall legalities.commander string ('legal', 'banned', ...).
  commander_legality?: string | null
  // null = singleton; -1 = any number; N = up to N copies.
  deck_limit?: number | null
  // Additive live-stats fields (mtg-api 2026-10-19). All optional, as above.
  // produced_mana is Scryfall's (fetch lands have none; is_fetch and
  // fetch_targets cover them). game_changer is the WotC disclosure list, not
  // a power score. function_category is the Card Breakdown function bucket.
  produced_mana?: string[] | null
  is_fetch?: boolean | null
  fetch_targets?: { colors?: string[]; basic_colors?: string[]; any_basic?: boolean; any_land?: boolean } | null
  game_changer?: boolean | null
  function_category?: string | null
  // The card's oracle text, top level (mtg-api 2026-10-20, additive and
  // optional). The editor's filter searches it with o:; absent on every row,
  // o: is skipped and the filter box says so.
  oracle_text?: string | null
}

interface BatchResponse {
  found: CardImage[]
  not_found: string[]
}

export interface CardImagesResult {
  found: CardImage[]
  notFound: string[]
  /**
   * Answered from the offline deck card store (offline contract W3), not
   * the API: a name it lacks is UNKNOWN here, never a confirmed not-found.
   */
  offline?: true
}

const MAX_BATCH = 120

// Collection scope for the gallery (owned printings). `collectionId`
// makes the server substitute the owned printing's art and price per name;
// `epoch` never reaches the wire, the hooks fold it into their query keys so
// a re-import refetches galleries. Absent or null id: the request is
// byte-identical to today's. Both CollectionScope and {} satisfy this type.
export interface CardImageScope {
  collectionId?: string | null
  epoch?: number | null
}

// Fetch card images for a deck. Dedupes, chunks at the endpoint's 120-name cap,
// and merges the chunk responses.
export async function fetchCardImages(names: string[], scope: CardImageScope = {}): Promise<CardImagesResult> {
  const unique = Array.from(new Set(names.map(n => n.trim()).filter(Boolean)))
  if (unique.length === 0) return { found: [], notFound: [] }
  // Offline (C1 not online): the stored metadata of the owner's deck cards.
  const offline = offlineCardSource()
  if (offline?.preferOffline()) {
    const answer = offlineAnswer(unique, offline.lookup)
    if (answer) return answer
  }
  const chunks: string[][] = []
  for (let i = 0; i < unique.length; i += MAX_BATCH) chunks.push(unique.slice(i, i + MAX_BATCH))
  const extra = scope.collectionId ? { collection_id: scope.collectionId } : {}
  let responses: BatchResponse[]
  try {
    responses = await Promise.all(
      chunks.map(c => apiPost<BatchResponse>('/cards/images', { names: c, ...extra })),
    )
  } catch (e) {
    // The request failed (offline, an outage): the stored metadata answers
    // what it can rather than nothing.
    const answer = offline ? offlineAnswer(unique, offline.lookup) : null
    if (answer) return answer
    throw e
  }
  return {
    found: responses.flatMap(r => r.found ?? []),
    notFound: responses.flatMap(r => r.not_found ?? []),
  }
}

/** The stored answer for `names`, or null when it knows none of them. */
function offlineAnswer(names: string[], lookup: (key: string) => CardImage | undefined): CardImagesResult | null {
  const found = new Map<string, CardImage>()
  for (const n of names) {
    const info = lookup(cardKey(n))
    if (info) found.set(info.name, info)
  }
  if (found.size === 0) return null
  return { found: [...found.values()], notFound: [], offline: true }
}

export function cardKey(name: string): string {
  return name.trim().toLowerCase()
}

// True when a card has exactly two distinct faces to flip between
// (transform / modal_dfc). Mirrors iOS CardImageInfo.isFlippable.
export function isFlippable(card: CardImage): boolean {
  return (card.faces?.length ?? 0) === 2
}

// Image URL for the requested face, preferring large, with face -> top-level
// fallbacks so a missing face image still renders something rather than going
// blank. Mirrors iOS CardImageLargeView.faceURL.
export function faceImageURL(card: CardImage, opts: { back?: boolean; large?: boolean } = {}): string | undefined {
  const { back = false, large = true } = opts
  const pick = (o?: { image_large?: string | null; image_normal?: string | null }): string | undefined =>
    (large ? o?.image_large ?? o?.image_normal : o?.image_normal ?? o?.image_large) ?? undefined
  if (isFlippable(card)) {
    return pick(card.faces![back ? 1 : 0]) ?? pick(card)
  }
  return pick(card)
}

// Index found cards under every name a decklist might use to refer to them:
// the canonical name, the front-face of a "Front // Back" DFC name, and each
// per-face name. Mirrors iOS CardImageService.index so a deck listing only a
// DFC's front face still resolves its type_line + image.
export function buildCardImageIndex(result?: CardImagesResult): Map<string, CardImage> {
  const index = new Map<string, CardImage>()
  for (const card of result?.found ?? []) {
    index.set(cardKey(card.name), card)
    const slash = card.name.indexOf(' // ')
    if (slash >= 0) index.set(cardKey(card.name.slice(0, slash)), card)
    for (const face of card.faces ?? []) index.set(cardKey(face.name), card)
  }
  return index
}
